<script setup lang="ts">
import { EmbeddedDropdownTransition, useClickOutside, globalOverlayContainer } from '#shared'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { computed, onBeforeMount, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue'
import { AlertsDropdown, useMarkAlertsAsReadMutation } from '#widgets/alerts-dropdown'
import { alertsQueryOptions } from '#widgets/alerts-dropdown/state/alerts-query'
import { useDynamicAlertUpdates } from '#widgets/alerts-dropdown/state/use-dynamic-alert-updates'

const alertsToggleButtonEl = document.querySelector<HTMLElement>('[data-behavior~="chat/alerts-toggle"]')
const isAlertsShown = ref(false)

const alertsQuery = useInfiniteQuery(alertsQueryOptions)
const unreadCount = computed(() => {
  const pages = alertsQuery.data.value?.pages
  if (!pages) return 0
  return Math.max(...pages.map(page => page.unreadCount), 0)
})

useDynamicAlertUpdates()

const markAlertsAsReadMutation = useMarkAlertsAsReadMutation()
watch([isAlertsShown, unreadCount], ([isAlertsShown, unreadCount]) => {
  if (isAlertsShown && unreadCount) {
    markAlertsAsReadMutation.mutate()
  }
})

const onAlertsToggle = () => {
  isAlertsShown.value = !isAlertsShown.value
}
const onAlertsClose = () => {
  isAlertsShown.value = false
}
onBeforeMount(() => {
  alertsToggleButtonEl?.addEventListener('click', onAlertsToggle)
})
onBeforeUnmount(() => {
  alertsToggleButtonEl?.removeEventListener('click', onAlertsToggle)
})

watch(
  unreadCount,
  unreadCount => {
    if (!alertsToggleButtonEl) return

    if (unreadCount) {
      alertsToggleButtonEl.classList.add('has-new-alerts')
      alertsToggleButtonEl.dataset.notificationCount = unreadCount.toString()
    } else {
      alertsToggleButtonEl.classList.remove('has-new-alerts')
    }
  },
  { immediate: true }
)

const alertsDropdown = useTemplateRef('alertsDropdown')
useClickOutside(
  computed(() => [alertsToggleButtonEl, alertsDropdown.value?.$el]),
  () => (isAlertsShown.value = false)
)
</script>

<template>
  <Teleport :to="globalOverlayContainer">
    <EmbeddedDropdownTransition>
      <AlertsDropdown
        v-if="isAlertsShown && alertsToggleButtonEl"
        :toggleEl="alertsToggleButtonEl"
        ref="alertsDropdown"
        @close="onAlertsClose"
      />
    </EmbeddedDropdownTransition>
  </Teleport>
</template>

<style lang="scss">
@use '#mixins/notification' as notification;
@use '#mixins/button' as button;

[data-behavior~='chat/alerts-toggle'] {
  @include button.reset;

  & {
    background-color: transparent;
    color: white;
  }

  &.has-new-alerts {
    @include notification.regular;
  }
}
</style>
